import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";

const TWEAKS_STYLE = `
  .twk-panel{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:280px;
    max-height:calc(100vh - 32px);display:flex;flex-direction:column;
    background:rgba(250,249,247,.78);color:#29261b;
    -webkit-backdrop-filter:blur(24px) saturate(160%);backdrop-filter:blur(24px) saturate(160%);
    border:.5px solid rgba(255,255,255,.6);border-radius:14px;
    box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 12px 40px rgba(0,0,0,.18);
    font:11.5px/1.4 ui-sans-serif,system-ui,-apple-system,sans-serif;overflow:hidden}
  .twk-hd{display:flex;align-items:center;justify-content:space-between;
    padding:10px 8px 10px 14px;cursor:move;user-select:none}
  .twk-hd b{font-size:12px;font-weight:600;letter-spacing:.01em}
  .twk-x{appearance:none;border:0;background:transparent;color:rgba(41,38,27,.55);
    width:22px;height:22px;border-radius:6px;cursor:pointer;font-size:13px;line-height:1}
  .twk-x:hover{background:rgba(0,0,0,.06);color:#29261b}
  .twk-body{padding:2px 14px 14px;display:flex;flex-direction:column;gap:10px;
    overflow-y:auto;overflow-x:hidden;min-height:0}
  .twk-row{display:flex;flex-direction:column;gap:5px}
  .twk-lbl{display:flex;justify-content:space-between;align-items:baseline;
    color:rgba(41,38,27,.72)}
  .twk-lbl>span:first-child{font-weight:500}
  .twk-sect{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
    color:rgba(41,38,27,.45);padding:10px 0 0}
  .twk-sect:first-child{padding-top:0}
  .twk-chips{display:flex;gap:6px}
  .twk-chip{position:relative;appearance:none;flex:1;min-width:0;height:46px;
    padding:0;border:0;border-radius:6px;overflow:hidden;cursor:pointer;
    box-shadow:0 0 0 .5px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.06);
    transition:transform .12s cubic-bezier(.3,.7,.4,1),box-shadow .12s}
  .twk-chip:hover{transform:translateY(-1px);
    box-shadow:0 0 0 .5px rgba(0,0,0,.18),0 4px 10px rgba(0,0,0,.12)}
  .twk-chip[data-on="1"]{box-shadow:0 0 0 1.5px rgba(0,0,0,.85),
    0 2px 6px rgba(0,0,0,.15)}
  .twk-chip>span{position:absolute;top:0;bottom:0;right:0;width:34%;
    display:flex;flex-direction:column;box-shadow:-1px 0 0 rgba(0,0,0,.1)}
  .twk-chip>span>i{flex:1;box-shadow:0 -1px 0 rgba(0,0,0,.1)}
  .twk-chip>span>i:first-child{box-shadow:none}
  .twk-chip svg{position:absolute;top:6px;left:6px;width:13px;height:13px;
    filter:drop-shadow(0 1px 1px rgba(0,0,0,.3))}

  .twk-launcher{position:fixed;right:16px;bottom:16px;z-index:2147483645;
    display:inline-flex;align-items:center;gap:8px;
    padding:8px 13px 8px 11px;border:.5px solid rgba(0,0,0,.12);border-radius:999px;
    background:rgba(250,249,247,.82);color:#29261b;
    -webkit-backdrop-filter:blur(20px) saturate(160%);backdrop-filter:blur(20px) saturate(160%);
    box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 8px 22px rgba(0,0,0,.14);
    font:500 11.5px/1 ui-sans-serif,system-ui,-apple-system,sans-serif;
    letter-spacing:.01em;cursor:pointer;
    transition:transform .14s cubic-bezier(.3,.7,.4,1),box-shadow .14s}
  .twk-launcher:hover{transform:translateY(-1px);
    box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 12px 28px rgba(0,0,0,.18)}
  .twk-launcher svg{width:12px;height:12px;display:block;opacity:.7}
`;

function isLight(hex: string): boolean {
  const h = hex.replace("#", "");
  const x = h.length === 3 ? h.replace(/./g, (c) => c + c) : h.padEnd(6, "0");
  const n = parseInt(x.slice(0, 6), 16);
  if (Number.isNaN(n)) return true;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return r * 299 + g * 587 + b * 114 > 148000;
}

interface PaletteOption {
  name: string;
  ink: string;
  paper: string;
  accent: string;
}

export const PALETTES: PaletteOption[] = [
  { name: "Ink",    ink: "#0E1014", paper: "#FAF9F5", accent: "#0F9D58" },
  { name: "Sienna", ink: "#101418", paper: "#F4F2EE", accent: "#C75D2C" },
  { name: "Cobalt", ink: "#0F1320", paper: "#F4F4F7", accent: "#3B5BDB" },
  { name: "Brass",  ink: "#171513", paper: "#F5F1E9", accent: "#B58840" },
];

function applyPalette(p: PaletteOption) {
  const root = document.documentElement;
  root.style.setProperty("--brand-ink", p.ink);
  root.style.setProperty("--brand-paper", p.paper);
  root.style.setProperty("--accent", p.accent);
  const rgb = (() => {
    const h = p.accent.replace("#", "");
    const x = h.length === 3 ? h.replace(/./g, (c) => c + c) : h.padEnd(6, "0");
    const n = parseInt(x, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255].join(",");
  })();
  root.style.setProperty("--accent-soft", `rgba(${rgb},0.12)`);
  root.style.setProperty("--accent-fade", `rgba(${rgb},0.05)`);
}

const STORAGE_KEY = "bbc.palette";

function loadInitialPalette(): PaletteOption {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { name?: string };
      const found = PALETTES.find((p) => p.name === parsed.name);
      if (found) return found;
    }
  } catch {
    /* ignore */
  }
  return PALETTES[0]!;
}

export function usePalette() {
  const [palette, setPaletteState] = useState<PaletteOption>(loadInitialPalette);

  useEffect(() => {
    applyPalette(palette);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ name: palette.name }));
    } catch {
      /* ignore */
    }
  }, [palette]);

  return [palette, setPaletteState] as const;
}

interface PanelProps {
  palette: PaletteOption;
  onPaletteChange: (p: PaletteOption) => void;
}

export function TweaksPanel({ palette, onPaletteChange }: PanelProps) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const offsetRef = useRef({ x: 16, y: 16 });

  const clamp = useCallback(() => {
    const el = panelRef.current;
    if (!el) return;
    const PAD = 16;
    const maxRight = Math.max(PAD, window.innerWidth - el.offsetWidth - PAD);
    const maxBottom = Math.max(PAD, window.innerHeight - el.offsetHeight - PAD);
    offsetRef.current = {
      x: Math.min(maxRight, Math.max(PAD, offsetRef.current.x)),
      y: Math.min(maxBottom, Math.max(PAD, offsetRef.current.y)),
    };
    el.style.right = offsetRef.current.x + "px";
    el.style.bottom = offsetRef.current.y + "px";
  }, []);

  useEffect(() => {
    if (!open) return;
    clamp();
    window.addEventListener("resize", clamp);
    return () => window.removeEventListener("resize", clamp);
  }, [open, clamp]);

  const onDragStart = (e: ReactMouseEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const sx = e.clientX;
    const sy = e.clientY;
    const startRight = window.innerWidth - r.right;
    const startBottom = window.innerHeight - r.bottom;
    const move = (ev: MouseEvent) => {
      offsetRef.current = {
        x: startRight - (ev.clientX - sx),
        y: startBottom - (ev.clientY - sy),
      };
      clamp();
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  if (!open) {
    return (
      <>
        <style>{TWEAKS_STYLE}</style>
        <button
          type="button"
          className="twk-launcher"
          aria-label="Open Tweaks"
          onClick={() => setOpen(true)}
        >
          <svg
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="5" cy="3.2" r="1.6" />
            <path d="M5 4.8V13" />
            <circle cx="10" cy="9.8" r="1.6" />
            <path d="M10 1v7.2" />
          </svg>
          <span>Tweaks</span>
        </button>
      </>
    );
  }

  return (
    <>
      <style>{TWEAKS_STYLE}</style>
      <div
        ref={panelRef}
        className="twk-panel"
        style={{
          right: offsetRef.current.x,
          bottom: offsetRef.current.y,
        }}
      >
        <div className="twk-hd" onMouseDown={onDragStart}>
          <b>Tweaks</b>
          <button
            className="twk-x"
            aria-label="Close tweaks"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setOpen(false)}
          >
            ✕
          </button>
        </div>
        <div className="twk-body">
          <Section label="Theme">
            <Row label="Palette">
              <div className="twk-chips" role="radiogroup">
                {PALETTES.map((p) => {
                  const on = p.name === palette.name;
                  return (
                    <button
                      key={p.name}
                      type="button"
                      className="twk-chip"
                      role="radio"
                      aria-checked={on}
                      data-on={on ? "1" : "0"}
                      aria-label={p.name}
                      title={p.name}
                      style={{ background: p.ink }}
                      onClick={() => onPaletteChange(p)}
                    >
                      <span>
                        <i style={{ background: p.paper }} />
                        <i style={{ background: p.accent }} />
                      </span>
                      {on && (
                        <svg viewBox="0 0 14 14" aria-hidden="true">
                          <path
                            d="M3 7.2 5.8 10 11 4.2"
                            fill="none"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            stroke={isLight(p.ink) ? "rgba(0,0,0,.78)" : "#fff"}
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </Row>
          </Section>
        </div>
      </div>
    </>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <>
      <div className="twk-sect">{label}</div>
      {children}
    </>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="twk-row">
      <div className="twk-lbl">
        <span>{label}</span>
      </div>
      {children}
    </div>
  );
}
